import { useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
  TransitionChild,
  Switch,
  Field,
  Label,
} from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  HomeIcon,
  InformationCircleIcon,
  XMarkIcon,
  DocumentMagnifyingGlassIcon,
  QueueListIcon,
  CheckBadgeIcon,
  ShareIcon,
  UserIcon,
  ArrowDownOnSquareIcon,
  QuestionMarkCircleIcon,
  PowerIcon,
} from "@heroicons/react/24/outline";
// import Logo from "../assets/images/Diligent - 2025 Branding 2.svg";
import Home from "../assets/images/Home.png";
import Info from "../assets/images/information.png";
import Share from "../assets/images/share.png";
import Assessments from "../assets/images/Assessments.png";
import { useClerk, useUser } from "@clerk/clerk-react";
import Searchbar from "./Search";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
} from "@clerk/clerk-react";
import { Link, useLocation } from "react-router-dom";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useUserRole, isBackendViewOnlyUser } from "../utils/helperMethods";
import { getSwitchToV1 } from "../api/api-methods";
import Loader from "./Skeletons/Loader";
import { useGateValue } from "@statsig/react-bindings";
import Statsig from "statsig-js";
import { useGate } from "statsig-react";
import { UsersIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import {
  setHcUserId,
  setUserRights,
} from "../redux-store/features/userRightsSlice";
import {
  useCheckProvidersRequiredFields,
  useGetVendorsMe,
  useHypercomplyLogout,
  useLoggedInGetSponsorUserDetails,
} from "../custom-hooks/queries";
import VendorBanner from "./Provider/VendorBanner";
import { setVendorUserDetails } from "../redux-store/features/vendorUserDetailsSlice";
import { useDecodedToken } from "../custom-hooks/useToken";
import DualRole from "./Dropdowns/DualRole";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Sidebar({ children, userDetails, vendorUserRights }) {
  const userRole = useUserRole();
  const { isSignedIn, user } = useUser();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const diligentGate = useGate("diligent");
  const isDiligentEnabled = diligentGate.value;
  const role = user?.publicMetadata?.role;
  const dispatch = useDispatch();
  const { signOut } = useClerk();
  const { decoded } = useDecodedToken();

  const switchToV1 = async () => {
    if (enabled) {
      setLoading(true);
      const { token } = await getSwitchToV1();
      window.location.href = `https://avdiligent-staging-qa2.azurewebsites.net/Account/Login?V2=${token}`;
      setTimeout(() => {
        setLoading(false);
      }, 10000);
    }
  };

  const { data: checkProvidersRequiredFieldsData } =
    useCheckProvidersRequiredFields(role);
  const { data: vendorData } = useGetVendorsMe(userRole);
  const { data: loggedInSponsorDetails } =
    useLoggedInGetSponsorUserDetails(role);

  // Determine clerk id to pass to DualRole based on current portal
  const clerkIdFromVendor =
    vendorData?.clerk_id || vendorData?.user_id || vendorData?.hc_user_id;
  const clerkIdFromSponsor =
    loggedInSponsorDetails?.clerk_id || loggedInSponsorDetails?.hc_user_id;
  const dualRoleClerkId =
    userRole === "Provider Master User" || userRole === "Provider User"
      ? clerkIdFromVendor
      : userRole === "Sponsor Master User" || userRole === "Sponsor User"
      ? clerkIdFromSponsor
      : undefined;

  // Check if user has dual role enabled
  const isDualRoleEnabled =
    (userRole === "Sponsor User" || userRole === "Sponsor Master User")
      ? loggedInSponsorDetails?.isdual_role === true
      : (userRole === "Provider User" || userRole === "Provider Master User")
      ? vendorData?.isdual_role === true
      : false;

  useEffect(() => {
    switchToV1();
  }, [enabled]);

  // Extract sub-roles and rights from userDetails
  const userSubRoles = userDetails?.sub_role?.map((role) => role) || [];
  const userRights = userDetails?.rights?.map((right) => right) || [];
  const isBackendViewOnly = isBackendViewOnlyUser(userRole);
  const vendorRights =
    vendorUserRights?.provider_user_permissions?.map(
      (right) => right.permission
    ) || [];

  const vendorRoles =
    vendorUserRights?.provider_user_subrole?.map((role) => role.subrole) || [];

  useEffect(() => {
    dispatch(setUserRights(userRights));
    dispatch(setHcUserId(userDetails?.hc_user_id));
    dispatch(setVendorUserDetails(vendorUserRights));
  }, [userDetails]);

  // Helper function to check if user has a specific right
  const hasRight = (rightName) => {
    return userRights.includes(rightName);
  };

  const hasVendorRights = (rightName) => {
    return vendorRights?.includes(rightName);
  };

  const hasSubRole = (rightName) => {
    return userSubRoles.includes(rightName);
  };
  const hasVendorSubRole = (roleName) => {
    return vendorRoles.includes(roleName);
  };

  // Define access conditions for different navigation sections
  const canAccessDocumentLibrary =
    hasRight("View and Request Library VQAs") || hasRight("Find Providers");

  const canAccessVendors = hasRight("Find Providers");

  const canAccessRFI =
    hasRight("View Requested RFIs") ||
    hasRight("Request RFIs") ||
    hasRight("Compare RFI") ||
    hasRight("View Released RFIs");

  const canAccessAssessments =
    hasRight("Request Assessment") ||
    hasRight("Shared Assessment Calendar") ||
    userSubRoles.includes("VQA Manager");

  const canManageUsers =
    hasRight("Manage Users") || userSubRoles.includes("Sponsor Admin");

  const isVQAManger = userDetails?.sub_role?.includes("VQA Manager");
  const isRFIManger = userDetails?.sub_role?.includes("RFI Manager");
  const isLibraryManger = userDetails?.sub_role?.includes("Library Manager");
  const isSponsorAdmin = userDetails?.sub_role?.includes("Sponsor Admin");

  const navigation = [
    ...(hasRight("Status Dashboard")
      ? [
          {
            name: "Dashboard",
            href: "#",
            icon: HomeIcon,
            current: false,
            subNavigation: [
              { name: "Status", href: "/", current: false },
              ...(hasRight("Trial Experience Dashboard")
                ? [
                    {
                      name: "Trial Experience",
                      href: "/TrialExperience",
                      current: false,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // ...(hasRight("Vendor Discovery")
    //   ? [
    //       {
    //         name: "Vendor Discovery",
    //         href: "/Providers",
    //         icon: HomeIcon,
    //         current: false,
    //       },
    //     ]
    //   : []),

    // Document Library section - conditionally included
    ...(hasRight("Document Library")
      ? [
          {
            name: "Document Library",
            href: "#",
            icon: DocumentMagnifyingGlassIcon,
            current: false,
            subNavigation: [
              { name: "Document Library", href: "/Documents", current: false },
              {
                name: "Library Requests",
                href: "/MyDocuments",
                current: false,
              },
            ],
          },
        ]
      : []),

    // Vendors section - conditionally included
    // ...(hasRight("Compare Vendors")
    //   ? [
    {
      name: "Vendors",
      href: "#",
      icon: ShareIcon,
      current: false,
      subNavigation: [
        ...(hasRight("Vendor Discovery")
          ? [
              {
                name: "Search Vendors",
                href: "/Providers",
                current: false,
              },
            ]
          : []),
        // { name: "Compare Vendors", href: "#", current: false },
        {
          name: "Request New Vendor",
          href: "/Provider/Create",
          current: false,
        },
      ],
    },
    //   ]
    // : []),

    // RFI section - conditionally included
    ...(hasRight("View Released RFIs")
      ? [
          {
            name: "Questionnaires",
            href: "#",
            icon: QueueListIcon,
            current: false,
            subNavigation: [
              ...(hasRight("View Requested RFIs")
                ? [{ name: "My VQQs", href: "/RFI/History", current: false }]
                : []),
              ...(hasRight("View Released RFIs")
                ? [
                    {
                      name: "VQQ Library",
                      href: "/documents/RFI",
                      current: false,
                    },
                  ]
                : []),

              ...(hasRight("Request RFIs")
                ? [{ name: "Request VQQ", href: "/RFI/Create", current: false }]
                : []),
              ...(hasRight("RFI Template Builder")
                ? [
                    // { name: "Risk Categorization", href: "#", current: false },
                    {
                      name: "Templates",
                      href: "/RFI/Builder",
                      current: false,
                    },
                  ]
                : []),
              ...(hasRight("My Vendors")
                ? [
                    {
                      name: "My Vendors",
                      href: "/Vendors",
                      current: false,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    // Assessments section - conditionally included
    ...(isSponsorAdmin || isVQAManger
      ? [
          {
            name: "Assessments",
            href: "#",
            icon: CheckBadgeIcon,
            current: false,
            subNavigation: [
              ...(hasRight("View My Assessment")
                ? [
                    {
                      name: "My Assessments",
                      href: "/Assessment/History",
                      current: false,
                    },
                  ]
                : []),
              ...(hasRight("View My Assessment")
                ? [
                    {
                      name: "Shared Assessments",
                      href: "/RequestAssesmentList",
                      icon: CheckBadgeIcon,
                      current: false,
                    },
                  ]
                : []),
              ...(hasRight("View Audit Reports")
                ? [
                    {
                      name: "Audit Report Library",
                      href: "/Documents/Audit",
                      current: false,
                    },
                  ]
                : []),
              ...(hasRight("Request/Manage New Assessments")
                ? [
                    {
                      name: "Request New Assessment",
                      href: "/Assessment/Create",
                      current: false,
                    },
                  ]
                : []),
              ...(hasRight("Join Scheduled Assessments")
                ? [
                    {
                      name: "Assessment Calendar",
                      href: "/Assessment/Calendar",
                      current: false,
                    },
                  ]
                : []),
              // ...(userSubRoles.includes("VQA Manager")
              //   ? [
              //       {
              //         name: "Join Scheduled Assessment",
              //         href: "#",
              //         current: false,
              //       },
              //     ]
              //   : []),
            ],
          },
        ]
      : []),
  ];

  // Request capability names to hide for Backend view-only (not applied to Sponsor Portal – Sponsors keep "Request New Vendor")
  const REQUEST_NAV_NAMES = [
    "Library Requests",
    "Request VQQ",
    "Request New Assessment",
  ];

  // Filter out Request items only for Backend User / Backend Master User (view-only). Sponsor Admin keeps full request capabilities.
  const filteredNavigation = navigation
    .filter((item) => !item.subNavigation || item.subNavigation.length > 0)
    .map((item) => {
      if (!isBackendViewOnly || !item.subNavigation) return item;
      const filteredSub = item.subNavigation.filter(
        (sub) => !REQUEST_NAV_NAMES.includes(sub.name)
      );
      return { ...item, subNavigation: filteredSub };
    })
    .filter((item) => !item.subNavigation || item.subNavigation.length > 0);

  const backendNavigation = [
    {
      name: "Dashboard",
      href: "/",
      icon: HomeIcon,
      current: false,
    },
    // Expose Discovery, Document Library, Assessment Calendar for Backend User / Backend Master User (view-only, no Request links)
    ...(userRole === "Backend User" || userRole === "Backend Master User"
      ? [
          {
            name: "Vendors",
            href: "#",
            icon: ShareIcon,
            current: false,
            subNavigation: [
              { name: "Search Vendors", href: "/Providers", current: false },
            ],
          },
          {
            name: "Document Library",
            href: "#",
            icon: DocumentMagnifyingGlassIcon,
            current: false,
            subNavigation: [
              { name: "Document Library", href: "/Documents", current: false },
            ],
          },
          {
            name: "Assessments",
            href: "#",
            icon: CheckBadgeIcon,
            current: false,
            subNavigation: [
              {
                name: "Assessment Calendar",
                href: "/Assessment/Calendar",
                current: false,
              },
              ...(isDiligentEnabled
                ? [
                    {
                      name: "Assessment Requests",
                      href: "/Backend/Assessment/Request/History",
                      current: false,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    ...(isDiligentEnabled &&
    userRole !== "Backend User" &&
    userRole !== "Backend Master User"
      ? [
          {
            name: "Assessments",
            href: "/Backend/Assessment/Request/History",
            icon: CheckBadgeIcon,
            current: false,
          },
        ]
      : []),
    {
      name: "Library Requests",
      href: "/Request/Library",
      icon: DocumentMagnifyingGlassIcon,
      current: false,
    },
    {
      name: "VQQs",
      href: "/Backend/Rfis",
      icon: QueueListIcon,
      current: false,
    },
    {
      name: "Users",
      href: "#",
      icon: UserIcon,
      current: false,
      subNavigation: [
        { name: "Vendors", href: "/Backend/Users/Vendors", current: false },

        { name: "Sponsors", href: "/Backend/Users/Sponsors", current: false },
        {
          name: "Administrators",
          href: "/Backend/Users/BackendUsers",
          current: false,
        },
      ],
    },
  ];

  const providerNavigation = [
    ...(hasVendorRights("Status Dashboard")
      ? [
          {
            name: "Dashboard",
            href: "/",
            icon: HomeIcon,
            current: false,
          },
        ]
      : []),

    ...(hasVendorSubRole("Vendor Admin") || hasVendorSubRole("RFI Manager - Approver") || hasVendorSubRole("RFI Manager - User")
      ? [
          {
            name: "Questionnaires",
            href: "#",
            icon: QueueListIcon,
            current: false,
            subNavigation: [
              ...(hasVendorRights("RFI Requests") ||
              hasVendorRights("Diligent RFI Requests")
                ? [
                    {
                      name: "Diligent Requests",
                      href: "/RFI/Requests",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Knowledge Base")
                ? [
                    {
                      name: "Knowledge Base",
                      href: "/Knowledgebase",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Questionnaire") ||
              hasVendorRights("All Questionnaires")
                ? [
                    {
                      name: "All Questionnaires",
                      href: "/Questionnaires",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Segmentation")
                ? [
                    {
                      name: "Segmentation",
                      href: "/Segmentation",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Documents")
                ? [
                    {
                      name: "Documents",
                      href: "/Documents",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Data Rooms")
                ? [
                    {
                      name: "Data Rooms",
                      href: "/DataRooms",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Trust Pages")
                ? [
                    {
                      name: "Trust Pages",
                      href: "/TrustPages",
                      current: false,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasVendorSubRole("Vendor Admin") || hasVendorSubRole("VQA Manager")
      ? [
          {
            name: "Assessments",
            href: "#",
            icon: CheckBadgeIcon,
            current: false,
            subNavigation: [
              ...(hasVendorRights("Library Requests")
                ? [
                    {
                      name: "Library Requests",
                      href: "/Assessment/Requests",
                      current: false,
                    },
                  ]
                : []),
              ...(hasVendorRights("Assessment Requests")
                ? [
                    {
                      name: "Assessment Requests",
                      href: "/Provider/Assessment/History",
                      current: false,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const navigationAccordingToRoles =
    userRole === "Sponsor User" || userRole === "Sponsor Master User"
      ? filteredNavigation // Use the filtered navigation for sponsors
      : userRole === "Provider User" || userRole === "Provider Master User"
      ? providerNavigation
      : backendNavigation;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const updatedNavigation = navigationAccordingToRoles.map((item) => {
    const isCurrent = location.pathname === item.href;

    // Sort subNavigation alphabetically by name
    const updatedSubNavigation = item.subNavigation
      ?.slice() // Create a shallow copy to avoid mutating the original array
      .map((subItem) => {
        const isSubCurrent = location.pathname === subItem.href;
        return { ...subItem, current: isSubCurrent };
      });

    return {
      ...item,
      current: isCurrent,
      subNavigation: updatedSubNavigation || item.subNavigation,
    };
  });

  const [searchProviders, setSearchProviders] = useState([]);

  const userNavigation = [
    { name: "Your Profile", href: "#" },
    { name: "Settings", href: "#" },
    { name: "Sign out", href: "#" },
  ];

  const { mutate: hyperComplySignout } = useHypercomplyLogout();

  const handleSignOut = () => {
    if (userRole === "Backend User") {
      signOut();
    } else {
      hyperComplySignout(
        { email: user?.primaryEmailAddress?.emailAddress },
        {
          onSuccess: () => {
            localStorage.setItem("reloadIframe", "true");

            sessionStorage.removeItem("sso_state");
            signOut();
          },
        }
      );
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="bg-custom-grey-color">
        <Transition show={sidebarOpen}>
          <Dialog className="relative z-40 lg:hidden" onClose={setSidebarOpen}>
            <TransitionChild
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div className="fixed inset-0 flex">
              <TransitionChild
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <TransitionChild
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </TransitionChild>
                  <div className="bg-custom-grey-color flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4 border-r border-gray-300">
                    <div className="flex h-16 shrink-0 justify-center items-center mt-5">
                      <img
                        src="/diligent-2025-branding-2.png"
                        alt="Diligent Pharma Logo"
                        height={75}
                        width={150}
                        style={{
                          objectFit: "contain",
                          maxWidth: "100%",
                          maxHeight: 75,
                        }}
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {updatedNavigation.map((item) => (
                              <li key={item.name}>
                                {!item.subNavigation ? (
                                  <Link
                                    to={item.href}
                                    className={classNames(
                                      item.current
                                        ? "flex items-center bg-custom-link-bg-color-10 text-custom-link-color"
                                        : "flex items-center text-gray-700 hover:bg-custom-link-bg-color-10 hover:text-indigo-600",
                                      "flex items-center group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left"
                                    )}
                                  >
                                    <item.icon
                                      className={classNames(
                                        item.current
                                          ? "text-indigo-600"
                                          : "text-gray-400 group-hover:text-indigo-600",
                                        "h-6 w-6 shrink-0"
                                      )}
                                      aria-hidden="true"
                                    />
                                    {item.name}
                                  </Link>
                                ) : (
                                  <Disclosure as="div" className="relative">
                                    {({ open }) => (
                                      <>
                                        <DisclosureButton
                                          className={classNames(
                                            item.current
                                              ? "bg-custom-link-bg-color-10 text-custom-link-color"
                                              : "text-gray-700 hover:bg-custom-link-bg-color-10 hover:text-indigo-600",
                                            "group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-semibold"
                                          )}
                                        >
                                          <item.icon
                                            className={classNames(
                                              item.current
                                                ? "text-indigo-600"
                                                : "text-gray-400 group-hover:text-indigo-600",
                                              "h-6 w-6 shrink-0"
                                            )}
                                            aria-hidden="true"
                                          />
                                          {item.name}
                                          <ChevronDownIcon
                                            className={classNames(
                                              open ? "rotate-180" : "",
                                              "ml-auto h-5 w-5 shrink-0 text-gray-400 transition-transform"
                                            )}
                                            aria-hidden="true"
                                          />
                                        </DisclosureButton>
                                        <DisclosurePanel
                                          as="ul"
                                          className="ml-6 mt-2 space-y-1"
                                        >
                                          {item.subNavigation.map((subItem) => (
                                            <li key={subItem.name}>
                                              <Link
                                                to={subItem.href}
                                                className={classNames(
                                                  subItem.current
                                                    ? "bg-custom-link-bg-color-10 text-custom-link-color"
                                                    : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                                                  "block rounded-md py-2 pl-9 pr-2 text-sm font-normal"
                                                )}
                                              >
                                                {subItem.name}
                                              </Link>
                                            </li>
                                          ))}
                                        </DisclosurePanel>
                                      </>
                                    )}
                                  </Disclosure>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-72 lg:flex-col border-r border-gray-300 ">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-custom-grey-color px-6 pb-4">
            <div className="flex h-16 shrink-0 justify-center items-center mt-5">
              <img
                width={150}
                height={75}
                src="/diligent-2025-branding-2.png"
                alt="Your Company"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {updatedNavigation.map((item) => (
                      <li key={item.name}>
                        {!item.subNavigation ? (
                          <>
                            <Link
                              to={item.href}
                              className={classNames(
                                item.current
                                  ? "bg-custom-link-bg-color-10 text-custom-link-color"
                                  : "text-gray-700 hover:bg-custom-link-bg-color-10 hover:text-indigo-600",
                                "flex items-center group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left"
                              )}
                            >
                              <item.icon
                                className={classNames(
                                  item.current
                                    ? "text-indigo-600"
                                    : "text-gray-400 group-hover:text-indigo-600",
                                  "h-6 w-6 shrink-0"
                                )}
                                aria-hidden="true"
                              />
                              {item.name}
                            </Link>
                          </>
                        ) : (
                          <Disclosure as="div" className="relative">
                            {({ open }) => (
                              <>
                                <DisclosureButton
                                  className={classNames(
                                    item.current
                                      ? "bg-custom-link-bg-color-10 text-custom-link-color"
                                      : "text-gray-700 hover:bg-custom-link-bg-color-10 hover:text-indigo-600",
                                    "group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-semibold"
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-indigo-600"
                                        : "text-gray-400 group-hover:text-indigo-600",
                                      "h-6 w-6 shrink-0"
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? "rotate-180" : "",
                                      "ml-auto h-5 w-5 shrink-0 text-gray-400 transition-transform"
                                    )}
                                    aria-hidden="true"
                                  />
                                </DisclosureButton>
                                <DisclosurePanel
                                  as="ul"
                                  className="ml-6 mt-2 space-y-1"
                                >
                                  {item.subNavigation.map((subItem) => (
                                    <li key={subItem.name}>
                                      <Link
                                        to={subItem.href}
                                        className={classNames(
                                          subItem.current
                                            ? "bg-custom-link-bg-color-10 text-custom-link-color"
                                            : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                                          "block rounded-md py-2 pl-9 pr-2 text-sm font-normal"
                                        )}
                                      >
                                        {subItem.name}
                                      </Link>
                                    </li>
                                  ))}
                                </DisclosurePanel>
                              </>
                            )}
                          </Disclosure>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
                {/* {(userRole === "Backend User" ||
                  userRole === "Backend Master User") && (
                  <Field className="flex items-center">
                    <Switch
                      checked={enabled}
                      onChange={setEnabled}
                      className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 data-[checked]:bg-indigo-600"
                    >
                      <span
                        aria-hidden="true"
                        className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                      />
                    </Switch>
                    <Label as="span" className="ml-3 text-sm">
                      <span className="font-medium text-gray-900">
                        Switch to V1
                      </span>{" "}
                    </Label>
                  </Field>
                )} */}
              </ul>
            </nav>  
          </div>
        </div>
        {/* {(userRole === "Provider Master User" ||
          userRole === "Provider User") &&
          checkProvidersRequiredFieldsData && (
            <div className="mt-[-22px]">
              <VendorBanner />
            </div>
          )} */}
        <div className="bg-white lg:pl-72 border-b border-gray-300">
          <div className="bg-white sticky top-0 z-40 lg:mx-auto lg:px-4">
            <div className="flex h-16 items-center bg-white px-2 shadow-sm sm:px-6 lg:px-0 lg:shadow-none">
              {/* Sidebar Toggle */}
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Main Flex Container */}
              <div className="flex flex-1 items-stretch justify-end divide-x divide-gray-300">
                {/* Portal Title */}
                {isSignedIn && (
                  <div className="flex items-center px-4">
                    <h2 className="font-semibold text-indigo-600">
                      {userRole === "Sponsor Master User" ||
                      userRole === "Sponsor User"
                        ? "Sponsor Portal"
                        : userRole === "Provider Master User" ||
                          userRole === "Provider User"
                        ? "Vendor Portal"
                        : userRole === "Backend Master User" ||
                          userRole === "Backend User"
                        ? "Admin Portal"
                        : ""}
                    </h2>
                  </div>
                )}

                {/* Help Center */}
                <a
                  href="https://support.diligentpharma.com/hc/en-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 text-sm font-medium text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
                  Help Center
                </a>

                {/* Clerk Buttons */}
                {/* {userRole !== "Sponsor User" && (
                  <div className="flex items-center px-4">
                    <SignedOut redirectUrl="/">
                      <SignInButton style={{ marginRight: "20px" }} />
                      <SignUpButton />
                    </SignedOut>

                    <SignedIn redirectUrl="/discovery">
                      <UserButton />
                    </SignedIn>
                  </div>
                )} */}
                {/* User Role Dropdown */}

                <div className="flex items-center px-4">
                  <Menu as="div" className="relative">
                    <MenuButton className="flex items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <span className="sr-only">User menu</span>
                      <span className="font-bold mr-2">{user?.fullName}</span>
                      <ChevronDownIcon className="h-5 w-5 text-gray-300 hover:text-indigo-600" />
                    </MenuButton>

                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="border border-gray-300 absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        {(userRole === "Sponsor User" ||
                          userRole === "Provider User") && (
                          <MenuItem>
                            {({ active }) => (
                              <Link
                                to={`${
                                  userRole === "Sponsor User"
                                    ? "/Sponsor/Users/Me"
                                    : "/Provider/Users/Me"
                                }`}
                                className={classNames(
                                  active ? "bg-gray-50" : "",
                                  "flex items-center px-4 py-2 text-sm text-gray-700"
                                )}
                              >
                                <UserIcon className="mr-3 h-5 w-5 text-gray-400" />
                                My Profile
                              </Link>
                            )}
                          </MenuItem>
                        )}
                        {(userRole === "Provider Master User" ||
                          userRole === "Provider User") && (
                          <MenuItem>
                            {({ active }) => (
                              <Link
                                to={`/user/team/profile/edit?vendorId=${decoded?.provider_id}`}
                                className={classNames(
                                  active ? "bg-gray-50" : "",
                                  "flex items-center px-4 py-2 text-sm text-gray-700"
                                )}
                              >
                                <UserIcon className="mr-3 h-5 w-5 text-gray-400" />
                                Manage Team
                              </Link>
                            )}
                          </MenuItem>
                        )}

                        {isSponsorAdmin && (
                          <MenuItem>
                            {({ active }) => (
                              <Link
                                to={`/Sponsor/Team/Manage?sponsorId=${decoded?.sponsor_id}&name=${decoded?.sponsor_name}`}
                                className={classNames(
                                  active ? "bg-gray-50" : "",
                                  "flex items-center px-4 py-2 text-sm text-gray-700"
                                )}
                              >
                                <UsersIcon className="mr-3 h-5 w-5 text-gray-400" />
                                Manage Team
                              </Link>
                            )}
                          </MenuItem>
                        )}
                        <MenuItem>
                          {({ active }) => (
                            <div
                              onClick={() => handleSignOut()}
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "flex items-center px-4 py-2 text-sm text-gray-700 cursor-pointer"
                              )}
                            >
                              <PowerIcon className="mr-3 h-5 w-5 text-red-400" />
                              Sign Out
                            </div>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
                {(userRole === "Sponsor User" ||
                  userRole === "Provider User") &&
                  isDualRoleEnabled && (
                  <div>
                    <DualRole clerkUserId={dualRoleClerkId} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
